// sweepStatus.js — pure helpers behind the masthead's sweep line. No React,
// no network; Watch.js renders what these return. The persisted
// SWEEP_LAST_REPORT (watchApi.loadLastReport) is the input everywhere — the
// last WRITE run. Dry runs persist to SWEEP_LAST_DRY, which nothing here reads.

export const STALE_MIN = 90;                 // a report older than this during sweep hours is amber
export const FAIL_STREAK = 2;                // consecutive failures that turn the dot red and show the alert

// Same gate as netlify/functions/_ctgate.js (duplicated: CRA cannot import
// outside src/). 04 CT and 07–22 CT.
export const SWEEP_HOURS = new Set([4, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22]);
const fmt = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', hourCycle: 'h23', hour: '2-digit' });
export function chicagoHour(date) {
  const part = fmt.formatToParts(date).find((p) => p.type === 'hour');
  return Number(part ? part.value : NaN);
}

/** The instant a report describes: finishedAt, else sweptAt (older rows). ms or null. */
export function reportTime(report) {
  const iso = report?.finishedAt || report?.sweptAt;
  const ms = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(ms) ? ms : null;
}

export const failures = (report) => Math.max(0, Number(report?.consecutiveFailures) || 0);

/**
 * 'ok' | 'warn' | 'fail' | null (no report).
 *   fail  consecutiveFailures ≥ FAIL_STREAK
 *   warn  exactly one failure, or the report is older than STALE_MIN during
 *         sweep hours (Chicago), or ok is false with no streak recorded
 *   ok    otherwise
 */
export function dotState(report, now = new Date()) {
  if (!report) return null;
  const n = failures(report);
  if (n >= FAIL_STREAK) return 'fail';
  if (n === 1) return 'warn';
  const t = reportTime(report);
  const nowMs = now instanceof Date ? now.getTime() : Number(now);
  if (t !== null && SWEEP_HOURS.has(chicagoHour(new Date(nowMs))) && nowMs - t > STALE_MIN * 60_000) return 'warn';
  if (report.ok === false) return 'warn';
  return 'ok';
}

/** "just now" · "12 min ago" · "3 h ago" · "2 d ago"; '' when there is no time. */
export function agoLabel(iso, now = new Date()) {
  const ms = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(ms)) return '';
  const nowMs = now instanceof Date ? now.getTime() : Number(now);
  const s = Math.max(0, Math.round((nowMs - ms) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
}

const num = (v) => (Array.isArray(v) ? v.length : Math.max(0, Number(v) || 0));

/** "· 3 new · 5 updated" from writes; '' when nothing to say. The row is a write run's (dry runs persist to SWEEP_LAST_DRY, which the masthead never reads). */
export function countsLabel(report) {
  if (!report) return '';
  const w = report.writes || {};
  const parts = [];
  const created = num(w.newOpens);
  const updated = num(w.sources);
  if (created) parts.push(`${created} new`);
  if (updated) parts.push(`${updated} updated`);
  return parts.length ? `· ${parts.join(' · ')}` : '';
}

/** true when the report finished after the board last loaded (ms). A missing report is never newer. */
export function isNewerThan(report, lastLoadedAtMs) {
  const t = reportTime(report);
  if (t === null) return false;
  const base = Number(lastLoadedAtMs);
  return !Number.isFinite(base) || t > base;
}

/** The alert line, or null below the streak. */
export function alertText(report) {
  const n = failures(report);
  if (n < FAIL_STREAK) return null;
  return `Sweep failing · ${n} in a row · ${report.errorClass || 'error'}`;
}
